import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import LegalPageLayout from './LegalPageLayout';
import LoadingSpinner from '../common/LoadingSpinner';
import { verifyPrivacyRequest } from '../../services/privacyRequestService';

function useQueryToken() {
    const { search } = useLocation();
    return new URLSearchParams(search).get('token');
}

function extractSuccessPayload(data) {
    const root = data && typeof data === 'object' ? data.data || data : {};
    const email =
        root.email ||
        root.userEmail ||
        (typeof root.contactEmail === 'string' ? root.contactEmail : null) ||
        '';
    const requestTypeLabel =
        root.requestTypeLabel ||
        root.request_type_label ||
        (typeof root.requestType === 'string' ? root.requestType : null) ||
        'personal data';
    return { email, requestTypeLabel };
}

const PrivacyVerifyPage = () => {
    const token = useQueryToken();
    const trimmed = token && token.trim();
    const [phase, setPhase] = useState(() => (trimmed ? 'loading' : 'missing'));
    const [errorMessage, setErrorMessage] = useState(null);
    const [success, setSuccess] = useState({ email: '', requestTypeLabel: '' });

    useEffect(() => {
        let cancelled = false;

        async function run() {
            if (!trimmed) {
                setPhase('missing');
                return;
            }

            setPhase('loading');
            const result = await verifyPrivacyRequest({ token: trimmed });
            if (cancelled) return;

            if (result.ok) {
                const extracted = extractSuccessPayload(result.data);
                setSuccess({
                    email: extracted.email || 'your email address',
                    requestTypeLabel: extracted.requestTypeLabel || 'personal data'
                });
                setPhase('success');
            } else {
                setErrorMessage(
                    result.message ||
                        'This verification link is invalid or has expired. Please submit a new request.'
                );
                setPhase('error');
            }
        }

        run();
        return () => {
            cancelled = true;
        };
    }, [trimmed]);

    const renderBody = () => {
        if (phase === 'missing') {
            return (
                <div className="legal-content privacy-verify-page text-center">
                    <p className="mb-4" role="alert">
                        Invalid or missing verification link. Please submit a new request.
                    </p>
                    <Link to="/data-deletion" className="btn btn-primary">
                        Submit a new request
                    </Link>
                </div>
            );
        }

        if (phase === 'loading') {
            return (
                <div className="legal-content privacy-verify-page text-center py-5">
                    <p className="mb-3">Verifying your request...</p>
                    <LoadingSpinner />
                </div>
            );
        }

        if (phase === 'success') {
            return (
                <div className="legal-content privacy-verify-page">
                    <h1 className="h2 font-weight-bold text-center mb-4">Request Verified</h1>
                    <p>
                        Your {success.requestTypeLabel} request has been verified and submitted. We will process it
                        within 30 days and send a confirmation to {success.email} when complete.
                    </p>
                    <p className="text-muted">
                        If your data is held across third-party services (such as Meta/Instagram or data enrichment
                        providers), we will note this in our response. You may need to contact those services separately.
                    </p>
                    <p className="text-center mt-4 mb-0">
                        <Link to="/">Back to Home</Link>
                    </p>
                </div>
            );
        }

        return (
            <div className="legal-content privacy-verify-page text-center">
                <div className="alert alert-danger" role="alert">
                    {errorMessage}
                </div>
                <Link to="/data-deletion" className="btn btn-primary">
                    Submit a new request
                </Link>
            </div>
        );
    };

    return (
        <LegalPageLayout>
            <div className="legal-page">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-12 col-lg-8">
                            <nav aria-label="breadcrumb" className="mt-4">
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <Link to="/">Home</Link>
                                    </li>
                                    <li className="breadcrumb-item active" aria-current="page">
                                        Verify request
                                    </li>
                                </ol>
                            </nav>
                            {renderBody()}
                        </div>
                    </div>
                </div>
            </div>
        </LegalPageLayout>
    );
};

export default PrivacyVerifyPage;
